import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPut, getApiError } from '../../lib/api';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { Button } from '../../components/Button';
import { Money } from '../../components/Money';
import { PageScaffold } from '../../components/PageScaffold';
import { StatusPill } from '../../components/StatusPill';
import { cn } from '../../lib/cn';

interface Shop {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

type TierMode = 'marginal' | 'cliff';

interface BaremDto {
  shop: { id: string; code: string; name: string };
  ruleId: string | null;
  tierMode: TierMode;
  fallbackRatePct: string;
  qualifyingMin: string;
  targetMonthly: string | null;
  effectiveFrom: string;
  tiers: { threshold: string; ratePct: string }[];
}

// Local editable band — values are PERCENT strings for rate (e.g. "0.75").
interface BandRow {
  threshold: string; // MUR
  ratePct: string; // percent, e.g. "0.75"
}

export function CommissionSettingsPage(): JSX.Element {
  const [selectedShopId, setSelectedShopId] = useState<string | null>(null);
  const shops = useQuery({
    queryKey: ['shops-admin', 'commission'],
    queryFn: () => apiGet<Shop[]>('/shops'),
  });

  const activeShops = (shops.data ?? []).filter((s) => s.isActive);
  const shopId = selectedShopId ?? activeShops[0]?.id ?? null;

  return (
    <PageScaffold
      title="Commission barems"
      subtitle="Per-shop commission brackets — each band's rate applies only to its slice of monthly net sales (marginal, income-tax style)."
    >
      <div className="grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-4">
        <Card title="Shops" bodyClassName="p-2">
          {shops.isLoading ? (
            <div className="text-sm text-ink-3 p-3">Loading…</div>
          ) : (
            <div className="flex flex-col">
              {activeShops.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSelectedShopId(s.id)}
                  className={cn(
                    'text-left px-3 py-2 rounded text-sm transition flex items-center gap-2',
                    s.id === shopId ? 'bg-accent text-white' : 'text-ink-2 hover:bg-paper-2',
                  )}
                >
                  <span
                    className={cn(
                      'kc-mono text-xs',
                      s.id === shopId ? 'text-white/80' : 'text-ink-3',
                    )}
                  >
                    {s.code}
                  </span>
                  <span className="truncate">{s.name}</span>
                </button>
              ))}
            </div>
          )}
        </Card>

        {shopId ? (
          <BaremEditor key={shopId} shopId={shopId} />
        ) : (
          <Card>
            <div className="p-6 text-ink-3">No shops.</div>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <SpecialModelsCard />
        <OnlineRatesCard />
        <CrossSaleProductsCard />
      </div>
    </PageScaffold>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Condition (b) — direct-commission models (Torres, Swisspedic, Memory Gel…)
// ─────────────────────────────────────────────────────────────────────────────

interface SpecialModelDto {
  id: string;
  name: string;
  category: { id: string; name: string };
  variantCount: number;
  directCommissionPct: string | null;
}

function SpecialModelsCard(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const flagged = useQuery({
    queryKey: ['commission-special-models'],
    queryFn: () => apiGet<SpecialModelDto[]>('/commissions/special-models'),
  });
  const candidates = useQuery({
    queryKey: ['commission-special-models', 'search', search],
    queryFn: () => apiGet<SpecialModelDto[]>('/commissions/special-models', { search }),
    enabled: search.trim().length >= 2,
  });

  const setRate = useMutation({
    mutationFn: ({ modelId, pct }: { modelId: string; pct: number | null }) =>
      apiPut(`/commissions/models/${modelId}/direct-rate`, { directCommissionPct: pct }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission-special-models'] });
      qc.invalidateQueries({ queryKey: ['commission'] });
    },
  });

  const flaggedIds = new Set((flagged.data ?? []).map((m) => m.id));
  const results = (candidates.data ?? []).filter((m) => !flaggedIds.has(m.id));

  return (
    <Card
      title="Direct-commission models"
      subtitle="e.g. Torres, Swisspedic, Memory Gel — these sales never enter the shop pool. The sales girl earns the model's own rate (typically 2%), paid only once the order is fully paid AND delivered."
    >
      <div className="space-y-4">
        <Input
          label="Add a model"
          placeholder="Search models by name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search.trim().length >= 2 && (
          <div className="border border-rule rounded divide-y divide-rule-2 max-h-56 overflow-y-auto">
            {candidates.isLoading ? (
              <div className="text-xs text-ink-3 p-3">Searching…</div>
            ) : results.length === 0 ? (
              <div className="text-xs text-ink-3 p-3">No unflagged models match “{search}”.</div>
            ) : (
              results.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-ink truncate">{m.name}</div>
                    <div className="text-[11px] text-ink-3">
                      {m.category.name} · {m.variantCount} variant{m.variantCount === 1 ? '' : 's'}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={setRate.isPending}
                    onClick={() => {
                      setRate.mutate({ modelId: m.id, pct: 0.02 });
                      setSearch('');
                    }}
                  >
                    + Add at 2%
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {flagged.isLoading ? (
          <div className="text-sm text-ink-3">Loading…</div>
        ) : (flagged.data ?? []).length === 0 ? (
          <div className="text-sm text-ink-3 text-center py-3">
            No direct-commission models yet — search above to add Torres, Swisspedic, Memory Gel…
          </div>
        ) : (
          <div className="divide-y divide-rule-2 border-t border-rule-2">
            {(flagged.data ?? []).map((m) => (
              <SpecialModelRow key={m.id} model={m} onSave={(pct) => setRate.mutate({ modelId: m.id, pct })} saving={setRate.isPending} />
            ))}
          </div>
        )}
        {setRate.isError && <div className="text-xs text-negative">{getApiError(setRate.error).message}</div>}
      </div>
    </Card>
  );
}

function SpecialModelRow({
  model,
  onSave,
  saving,
}: {
  model: SpecialModelDto;
  onSave: (pct: number | null) => void;
  saving: boolean;
}): JSX.Element {
  const [pct, setPct] = useState<string>(pctStr(model.directCommissionPct ?? '0'));
  const dirty = pct !== pctStr(model.directCommissionPct ?? '0');
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink truncate">{model.name}</div>
        <div className="text-[11px] text-ink-3">
          {model.category.name} · {model.variantCount} variant{model.variantCount === 1 ? '' : 's'} · paid on full payment + delivery
        </div>
      </div>
      <div className="w-24">
        <Input
          type="number"
          step="0.1"
          min="0"
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          aria-label={`${model.name} direct rate %`}
        />
      </div>
      <span className="text-xs text-ink-3">%</span>
      {dirty && (
        <Button size="sm" loading={saving} onClick={() => onSave(Number(pct) / 100 || 0)}>
          Save
        </Button>
      )}
      <Button size="sm" variant="ghost" loading={saving} onClick={() => onSave(null)} title="Remove — back to normal pool commission">
        ✕
      </Button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Condition (c) — online-channel commission rates per category
// ─────────────────────────────────────────────────────────────────────────────

interface OnlineRateDto {
  id: string;
  code: string;
  name: string;
  onlineCommissionPct: string | null;
}

function OnlineRatesCard(): JSX.Element {
  const qc = useQueryClient();
  const rates = useQuery({
    queryKey: ['commission-online-rates'],
    queryFn: () => apiGet<OnlineRateDto[]>('/commissions/online-rates'),
  });
  const save = useMutation({
    mutationFn: ({ categoryId, pct }: { categoryId: string; pct: number | null }) =>
      apiPut(`/commissions/categories/${categoryId}/online-rate`, { onlineCommissionPct: pct }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission-online-rates'] });
      qc.invalidateQueries({ queryKey: ['commission'] });
    },
  });

  return (
    <Card
      title="Online sales commission"
      subtitle="Orders made on the Online channel pay the person who took them a per-category rate (e.g. 1% imported mattresses, 0.5% local) instead of the shop pool. Promo / discounted lines automatically pay half."
    >
      {rates.isLoading ? (
        <div className="text-sm text-ink-3">Loading…</div>
      ) : (
        <div className="divide-y divide-rule-2">
          {(rates.data ?? []).map((c) => (
            <OnlineRateRow key={c.id} cat={c} onSave={(pct) => save.mutate({ categoryId: c.id, pct })} saving={save.isPending} />
          ))}
        </div>
      )}
      {save.isError && <div className="text-xs text-negative mt-2">{getApiError(save.error).message}</div>}
    </Card>
  );
}

function OnlineRateRow({
  cat,
  onSave,
  saving,
}: {
  cat: OnlineRateDto;
  onSave: (pct: number | null) => void;
  saving: boolean;
}): JSX.Element {
  const [pct, setPct] = useState<string>(cat.onlineCommissionPct ? pctStr(cat.onlineCommissionPct) : '');
  const saved = cat.onlineCommissionPct ? pctStr(cat.onlineCommissionPct) : '';
  const dirty = pct !== saved;
  return (
    <div className="flex items-center gap-3 py-2.5">
      <div className="flex-1 min-w-0">
        <span className="kc-mono text-xs text-ink-3 mr-2">{cat.code}</span>
        <span className="text-sm text-ink">{cat.name}</span>
        {!cat.onlineCommissionPct && <StatusPill tone="neutral" className="ml-2">no online commission</StatusPill>}
      </div>
      <div className="w-24">
        <Input
          type="number"
          step="0.1"
          min="0"
          placeholder="—"
          value={pct}
          onChange={(e) => setPct(e.target.value)}
          aria-label={`${cat.name} online rate %`}
        />
      </div>
      <span className="text-xs text-ink-3">%</span>
      {dirty && (
        <Button
          size="sm"
          loading={saving}
          onClick={() => onSave(pct.trim() === '' ? null : Number(pct) / 100 || 0)}
        >
          Save
        </Button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Condition (d) — cross-sales accessories (protectors, pillows, toppers…)
// ─────────────────────────────────────────────────────────────────────────────

interface CrossSaleProductDto {
  id: string;
  sku: string;
  name: string;
  category: { id: string; name: string };
  crossSaleEligible: boolean;
}

function CrossSaleProductsCard(): JSX.Element {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const flagged = useQuery({
    queryKey: ['commission-cross-sale-products'],
    queryFn: () => apiGet<CrossSaleProductDto[]>('/commissions/cross-sale-products'),
  });
  const candidates = useQuery({
    queryKey: ['commission-cross-sale-products', 'search', search],
    queryFn: () => apiGet<CrossSaleProductDto[]>('/commissions/cross-sale-products', { search }),
    enabled: search.trim().length >= 2,
  });

  const setEligible = useMutation({
    mutationFn: ({ productId, eligible }: { productId: string; eligible: boolean }) =>
      apiPut(`/commissions/products/${productId}/cross-sale`, { crossSaleEligible: eligible }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission-cross-sale-products'] });
      qc.invalidateQueries({ queryKey: ['commission'] });
    },
  });

  const flaggedIds = new Set((flagged.data ?? []).map((p) => p.id));
  const results = (candidates.data ?? []).filter((p) => !p.crossSaleEligible && !flaggedIds.has(p.id));

  return (
    <Card
      title="Cross-sales accessories"
      subtitle="Protectors, pillows, toppers, duvets, bed sheets and other small items. When a shop misses its target the barem pool stays locked, but each girl still earns 5% on the eligible cross-sales she sold — only on invoices that also include a mattress (accessories-only invoices earn nothing). All small items are eligible by default."
    >
      <div className="space-y-4">
        <Input
          label="Add a product"
          placeholder="Search products by name or SKU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search.trim().length >= 2 && (
          <div className="border border-rule rounded divide-y divide-rule-2 max-h-56 overflow-y-auto">
            {candidates.isLoading ? (
              <div className="text-xs text-ink-3 p-3">Searching…</div>
            ) : results.length === 0 ? (
              <div className="text-xs text-ink-3 p-3">No ineligible products match “{search}”.</div>
            ) : (
              results.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <div className="text-sm text-ink truncate">{p.name}</div>
                    <div className="text-[11px] text-ink-3">
                      <span className="kc-mono">{p.sku}</span> · {p.category.name}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={setEligible.isPending}
                    onClick={() => {
                      setEligible.mutate({ productId: p.id, eligible: true });
                      setSearch('');
                    }}
                  >
                    + Mark eligible
                  </Button>
                </div>
              ))
            )}
          </div>
        )}

        {flagged.isLoading ? (
          <div className="text-sm text-ink-3">Loading…</div>
        ) : (flagged.data ?? []).length === 0 ? (
          <div className="text-sm text-ink-3 text-center py-3">
            No eligible cross-sales products yet — search above to add accessories.
          </div>
        ) : (
          <div className="divide-y divide-rule-2 border-t border-rule-2 max-h-80 overflow-y-auto">
            {(flagged.data ?? []).map((p) => (
              <div key={p.id} className="flex items-center gap-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink truncate">{p.name}</div>
                  <div className="text-[11px] text-ink-3">
                    <span className="kc-mono">{p.sku}</span> · {p.category.name} · earns 5% when shop misses target
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={setEligible.isPending}
                  onClick={() => setEligible.mutate({ productId: p.id, eligible: false })}
                  title="Remove — no cross-sales commission"
                >
                  ✕
                </Button>
              </div>
            ))}
          </div>
        )}
        {setEligible.isError && (
          <div className="text-xs text-negative">{getApiError(setEligible.error).message}</div>
        )}
      </div>
    </Card>
  );
}

function BaremEditor({ shopId }: { shopId: string }): JSX.Element {
  const qc = useQueryClient();
  const barem = useQuery({
    queryKey: ['commission-barem', shopId],
    queryFn: () => apiGet<BaremDto>(`/commissions/shops/${shopId}/barem`),
  });

  return barem.data ? (
    <BaremForm key={barem.data.ruleId ?? 'new'} shopId={shopId} data={barem.data} qc={qc} />
  ) : (
    <Card>
      <div className="p-6 text-ink-3">Loading barem…</div>
    </Card>
  );
}

function BaremForm({
  shopId,
  data,
  qc,
}: {
  shopId: string;
  data: BaremDto;
  qc: ReturnType<typeof useQueryClient>;
}): JSX.Element {
  // Commission is marginal-only by design; cliff is no longer offered in the UI.
  const tierMode: TierMode = 'marginal';
  const [fallbackPct, setFallbackPct] = useState<string>(pctStr(data.fallbackRatePct));
  const [qualifyingMin, setQualifyingMin] = useState<string>(
    data.qualifyingMin && Number(data.qualifyingMin) > 0 ? data.qualifyingMin : '',
  );
  const [targetMonthly, setTargetMonthly] = useState<string>(data.targetMonthly ?? '');
  const [bands, setBands] = useState<BandRow[]>(
    data.tiers.length > 0
      ? data.tiers.map((t) => ({ threshold: t.threshold, ratePct: pctStr(t.ratePct) }))
      : [{ threshold: '0', ratePct: '' }],
  );
  const [preview, setPreview] = useState<string>(data.targetMonthly ?? '2000000');

  const setBand = (i: number, patch: Partial<BandRow>): void =>
    setBands((b) => b.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const addBand = (): void => setBands((b) => [...b, { threshold: '', ratePct: '' }]);
  const removeBand = (i: number): void => setBands((b) => b.filter((_, idx) => idx !== i));

  // Validation: thresholds ascending, all numeric.
  const parsedBands = bands
    .filter((b) => b.threshold !== '' && b.ratePct !== '')
    .map((b) => ({ threshold: Number(b.threshold), ratePct: Number(b.ratePct) / 100 }));
  const ascending = parsedBands.every(
    (b, i) => i === 0 || b.threshold > parsedBands[i - 1]!.threshold,
  );
  const valid =
    ascending &&
    parsedBands.every((b) => Number.isFinite(b.threshold) && Number.isFinite(b.ratePct));

  const qualMin = Number(qualifyingMin) || 0;
  const previewResult = useMemo(
    () =>
      computePreview(
        Number(preview) || 0,
        tierMode,
        Number(fallbackPct) / 100 || 0,
        parsedBands,
        qualMin,
      ),
    [preview, tierMode, fallbackPct, parsedBands, qualMin],
  );

  const save = useMutation({
    mutationFn: () =>
      apiPut(`/commissions/shops/${shopId}/barem`, {
        tierMode,
        fallbackRatePct: Number(fallbackPct) / 100 || 0,
        qualifyingMin: qualMin,
        targetMonthly: targetMonthly === '' ? null : Number(targetMonthly),
        tiers: parsedBands,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['commission-barem', shopId] });
      qc.invalidateQueries({ queryKey: ['commission'] });
    },
  });

  return (
    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
      <Card
        title={`${data.shop.code} · ${data.shop.name}`}
        subtitle="Edit the commission brackets for this shop."
      >
        <div className="space-y-4">
          {/* Qualifying min + fallback + target */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Qualifying minimum (MUR)"
              type="number"
              min="0"
              value={qualifyingMin}
              onChange={(e) => setQualifyingMin(e.target.value)}
              hint="Below this, no commission. Blank/0 = no gate."
            />
            <Input
              label="Fallback rate (%)"
              type="number"
              step="0.01"
              min="0"
              value={fallbackPct}
              onChange={(e) => setFallbackPct(e.target.value)}
              hint="Applied below the first band"
            />
            <Input
              label="Monthly target (MUR)"
              type="number"
              min="0"
              value={targetMonthly}
              onChange={(e) => setTargetMonthly(e.target.value)}
              hint="For the progress bar (optional)"
            />
          </div>

          {/* Bands */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-ink-3 uppercase tracking-wide">
                Bands (lower bound → rate)
              </span>
              <Button size="sm" variant="ghost" onClick={addBand}>
                + Add band
              </Button>
            </div>
            <div className="space-y-2">
              {bands.map((b, i) => {
                const upper = bands[i + 1]?.threshold;
                return (
                  <div key={i} className="flex items-end gap-3">
                    <div className="flex-1">
                      <Input
                        label={i === 0 ? 'From (MUR)' : undefined}
                        type="number"
                        min="0"
                        value={b.threshold}
                        onChange={(e) => setBand(i, { threshold: e.target.value })}
                      />
                    </div>
                    <div className="text-ink-3 pb-2 text-xs whitespace-nowrap">
                      {upper !== undefined && upper !== '' ? (
                        <>→ {Number(upper).toLocaleString()}</>
                      ) : (
                        '→ ∞'
                      )}
                    </div>
                    <div className="w-28">
                      <Input
                        label={i === 0 ? 'Rate (%)' : undefined}
                        type="number"
                        step="0.01"
                        min="0"
                        value={b.ratePct}
                        onChange={(e) => setBand(i, { ratePct: e.target.value })}
                      />
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeBand(i)}
                      disabled={bands.length === 1}
                    >
                      ✕
                    </Button>
                  </div>
                );
              })}
            </div>
            {!ascending && (
              <div className="text-xs text-negative mt-2">
                Band thresholds must be in ascending order.
              </div>
            )}
          </div>

          {save.isError && (
            <div className="text-xs text-negative">{getApiError(save.error).message}</div>
          )}
          <div className="flex justify-end gap-2">
            <Button loading={save.isPending} disabled={!valid} onClick={() => save.mutate()}>
              Save barem
            </Button>
          </div>
          {save.isSuccess && <div className="text-xs text-positive text-right">Saved.</div>}
        </div>
      </Card>

      {/* Live preview */}
      <Card title="Preview" subtitle="What this barem would pay on a given monthly net.">
        <div className="space-y-3">
          <Input
            label="Monthly net sales (MUR)"
            type="number"
            min="0"
            value={preview}
            onChange={(e) => setPreview(e.target.value)}
            className="max-w-xs"
          />
          {!previewResult.qualified && (
            <div className="text-xs text-warning">
              Below the qualifying minimum (<Money value={qualMin} muted />) — earns nothing. Needs{' '}
              <Money value={Math.max(0, qualMin - (Number(preview) || 0))} muted /> more to qualify.
            </div>
          )}
          <div className="flex gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
                Commission
              </div>
              <div className="text-2xl kc-serif text-accent mt-1">
                <Money value={previewResult.commission} />
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-ink-3 font-semibold">
                Blended rate
              </div>
              <div className="text-2xl kc-serif text-ink mt-1 kc-mono">
                {(previewResult.blended * 100).toFixed(3)}%
              </div>
            </div>
          </div>
          {previewResult.bands.length > 0 && (
            <table className="w-full text-sm mt-2">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 border-y border-rule">
                  <th className="py-2 font-medium">Band</th>
                  <th className="py-2 font-medium text-right">Rate</th>
                  <th className="py-2 font-medium text-right">Slice</th>
                  <th className="py-2 font-medium text-right">Earns</th>
                </tr>
              </thead>
              <tbody>
                {previewResult.bands.map((band, i) => (
                  <tr key={i} className="border-b border-rule-2">
                    <td className="py-2 text-ink-2 kc-mono text-xs">
                      {band.from.toLocaleString()} →{' '}
                      {band.to === null ? '∞' : band.to.toLocaleString()}
                    </td>
                    <td className="py-2 text-right kc-mono">{(band.ratePct * 100).toFixed(2)}%</td>
                    <td className="py-2 text-right">
                      <Money value={band.slice} muted />
                    </td>
                    <td className="py-2 text-right">
                      <Money value={band.commission} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────────────────

// fraction string ("0.0075") → percent string ("0.75")
function pctStr(fraction: string): string {
  const n = Number(fraction);
  if (!Number.isFinite(n) || n === 0) return '';
  return String(Number((n * 100).toFixed(4)));
}

interface PreviewBand {
  from: number;
  to: number | null;
  ratePct: number;
  slice: number;
  commission: number;
}

// Mirror of the backend engine (apps/backend/src/modules/finance/commission.ts)
// for an instant client-side preview.
function computePreview(
  net: number,
  mode: TierMode,
  fallback: number,
  tiers: { threshold: number; ratePct: number }[],
  qualifyingMin = 0,
): { commission: number; blended: number; bands: PreviewBand[]; qualified: boolean } {
  const sorted = [...tiers].sort((a, b) => a.threshold - b.threshold);

  // Qualifying gate: below the minimum, the shop earns nothing.
  if (net < qualifyingMin) return { commission: 0, blended: 0, bands: [], qualified: false };

  if (mode === 'cliff') {
    let rate = fallback;
    for (const t of sorted) if (net >= t.threshold) rate = t.ratePct;
    const commission = net * rate;
    return {
      commission,
      blended: net > 0 ? commission / net : 0,
      bands: net > 0 ? [{ from: 0, to: null, ratePct: rate, slice: net, commission }] : [],
      qualified: true,
    };
  }

  const segments: { from: number; ratePct: number }[] = [];
  if (sorted.length === 0 || sorted[0]!.threshold > 0)
    segments.push({ from: 0, ratePct: fallback });
  for (const t of sorted) segments.push({ from: t.threshold, ratePct: t.ratePct });

  const bands: PreviewBand[] = [];
  let commission = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]!;
    const upper = i + 1 < segments.length ? segments[i + 1]!.from : null;
    const cappedUpper = upper === null ? net : Math.min(net, upper);
    const slice = Math.max(0, cappedUpper - seg.from);
    if (slice <= 0) continue;
    const c = slice * seg.ratePct;
    commission += c;
    bands.push({ from: seg.from, to: upper, ratePct: seg.ratePct, slice, commission: c });
  }
  return { commission, blended: net > 0 ? commission / net : 0, bands, qualified: true };
}
